import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import BuildingSearchField from './BuildingSearchField';
import {
  attachChild,
  attachParent,
  clearCurrent,
  clearError,
  clearMaster,
  setMaster,
  createBuilding,
  deleteBuilding,
  detachChild,
  detachParent,
  fetchBuilding,
  fetchStatuses,
  BuildingSummary,
  selectStatuses,
  selectTypes,
  selectBuilding,
  selectBuildingChildren,
  selectBuildingMaster,
  selectBuildingParents,
  selectBuildingSlaves,
  selectBuildingStatus,
  selectError,
  selectSaveStatus,
  updateBuilding,
} from '../features/buildingEditor/buildingEditor';

interface FormState {
  address: string;
  buildingName: string;
  uniqueCode: string;
  status: string;
  legacyType: string;
  buildingType: string;
  estimatedServiceDeliveryTime: string;
  numberOfLevels: string;
  numberOfDwellings: string;
  footprint: boolean;
  inductionRequired: boolean;
  customerInstallApprovalRequired: boolean;
}

const emptyForm: FormState = {
  address: '',
  buildingName: '',
  uniqueCode: '',
  status: '',
  legacyType: '',
  buildingType: '',
  estimatedServiceDeliveryTime: '',
  numberOfLevels: '',
  numberOfDwellings: '',
  footprint: false,
  inductionRequired: false,
  customerInstallApprovalRequired: false,
};

// older documents keep their type under "_type", newer ones under "type"
const LEGACY_TYPE_KEY = '_type';

const formFromDoc = (doc: Record<string, any>): FormState => ({
  address: doc.address || '',
  buildingName: doc.buildingName || '',
  uniqueCode: doc.uniqueCode || '',
  status: doc.status || '',
  legacyType: doc[LEGACY_TYPE_KEY] || doc.type || '',
  buildingType: doc.buildingType || '',
  estimatedServiceDeliveryTime: doc.estimatedServiceDeliveryTime || '',
  numberOfLevels: doc.numberOfLevels != null ? String(doc.numberOfLevels) : '',
  numberOfDwellings: doc.numberOfDwellings != null ? String(doc.numberOfDwellings) : '',
  footprint: !!doc.footprint,
  inductionRequired: !!doc.inductionRequired,
  customerInstallApprovalRequired: !!doc.customerInstallApprovalRequired,
});

// Only fields present in the form are sent; the backend $sets exactly these,
// so unknown fields on the document survive untouched.
const fieldsFromForm = (form: FormState, doc: Record<string, any> | null): Record<string, any> => {
  const fields: Record<string, any> = {
    address: form.address,
    buildingName: form.buildingName,
    uniqueCode: form.uniqueCode,
    status: form.status,
    buildingType: form.buildingType,
    estimatedServiceDeliveryTime: form.estimatedServiceDeliveryTime,
    footprint: form.footprint,
    inductionRequired: form.inductionRequired,
    customerInstallApprovalRequired: form.customerInstallApprovalRequired,
  };
  // preserve whichever key the document already uses for its type
  if (doc && doc[LEGACY_TYPE_KEY] !== undefined) {
    fields[LEGACY_TYPE_KEY] = form.legacyType;
  } else {
    fields.type = form.legacyType;
  }
  if (form.numberOfLevels !== '') {
    fields.numberOfLevels = Number(form.numberOfLevels);
  }
  if (form.numberOfDwellings !== '') {
    fields.numberOfDwellings = Number(form.numberOfDwellings);
  }
  return fields;
};

const RelationList = ({
  items,
  emptyText,
  onOpen,
  unlinkTooltip = undefined,
  onUnlink = undefined,
}: {
  items: BuildingSummary[];
  emptyText: string;
  onOpen: (id: string) => void;
  unlinkTooltip?: string;
  onUnlink?: (id: string) => void;
}) => (
  <List dense>
    {items.length === 0 && (
      <ListItem>
        <ListItemText secondary={emptyText} />
      </ListItem>
    )}
    {items.map((item) => (
      <ListItem
        key={item.id}
        secondaryAction={(
          <>
            <Tooltip title="Open">
              <IconButton edge="end" onClick={() => onOpen(item.id)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onUnlink && (
              <Tooltip title={unlinkTooltip || 'Detach'}>
                <IconButton edge="end" onClick={() => onUnlink(item.id)}>
                  <LinkOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      >
        <ListItemText
          primary={item.address || item.id}
          secondary={`${item.uniqueCode || 'no code'} · ${item.status || 'no status'}`}
        />
      </ListItem>
    ))}
  </List>
);

const BuildingEditorDetail = () => {
  const { buildingId } = useParams();
  const isNew = buildingId === 'new';
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const doc = useAppSelector(selectBuilding);
  const parents = useAppSelector(selectBuildingParents);
  const children = useAppSelector(selectBuildingChildren);
  const slaves = useAppSelector(selectBuildingSlaves);
  const master = useAppSelector(selectBuildingMaster);
  const status = useAppSelector(selectBuildingStatus);
  const saveStatus = useAppSelector(selectSaveStatus);
  const error = useAppSelector(selectError);

  const statuses = useAppSelector(selectStatuses);
  const types = useAppSelector(selectTypes);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [snack, setSnack] = useState<string | null>(null);

  useEffect(() => {
    dispatch(fetchStatuses());
  }, []);

  useEffect(() => {
    dispatch(clearCurrent());
    if (!isNew && buildingId) {
      dispatch(fetchBuilding(buildingId));
    }
  }, [buildingId]);

  useEffect(() => {
    if (doc) {
      setForm(formFromDoc(doc));
    }
  }, [doc]);

  const relatedIds = useMemo(
    () => [
      ...(buildingId && !isNew ? [buildingId] : []),
      ...parents.map((p) => p.id),
      ...children.map((c) => c.id),
    ],
    [buildingId, parents, children],
  );

  const setField = (key: keyof FormState) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.type === 'checkbox'
      ? event.target.checked
      : event.target.value;
    setForm((current) => ({ ...current, [key]: value }));
  };

  // the dictionary enum, always including the document's current value so
  // an out-of-dictionary status stays selectable rather than lost
  const statusOptions = useMemo(() => {
    const options = new Set(statuses);
    if (form.status) options.add(form.status);
    return Array.from(options);
  }, [statuses, form.status]);

  const typeOptions = useMemo(() => {
    const options = new Set(types);
    if (form.legacyType) options.add(form.legacyType);
    return Array.from(options);
  }, [types, form.legacyType]);

  const handleSave = async () => {
    if (isNew) {
      const action = await dispatch(createBuilding(fieldsFromForm(form, null)));
      if (createBuilding.fulfilled.match(action)) {
        setSnack('Building created');
        navigate(`/dashboard/building-editor/${action.payload.id}`, { replace: true });
      }
    } else if (buildingId) {
      const action = await dispatch(
        updateBuilding({ id: buildingId, fields: fieldsFromForm(form, doc) }),
      );
      if (updateBuilding.fulfilled.match(action)) {
        setSnack('Building saved');
      }
    }
  };

  // await a link/unlink thunk and confirm it visually on success
  const runRelation = async (thunk: any, message: string) => {
    const action = await dispatch(thunk);
    if (action.meta.requestStatus === 'fulfilled') {
      setSnack(message);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    if (!buildingId) return;
    const action = await dispatch(deleteBuilding(buildingId));
    if (deleteBuilding.fulfilled.match(action)) {
      navigate('/dashboard/building-editor');
    }
  };

  if (!isNew && status === 'loading' && !doc) {
    return (
      <Box sx={{ width: '100%' }}>
        <Toolbar />
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Toolbar />
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 2,
      }}
      >
        <IconButton onClick={() => navigate('/dashboard/building-editor')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">
          {isNew ? 'New Building' : form.address || 'Building'}
        </Typography>
        {!isNew && doc?.uniqueCode && <Chip label={doc.uniqueCode} />}
        {!isNew && doc?.addressComponents?.isSlave && (
          <Chip label="SLAVE" color="warning" size="small" />
        )}
        {!isNew && (doc?.addressComponents?.isMaster || slaves.length > 0) && (
          <Chip label="MASTER" color="primary" size="small" />
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}
      {!isNew && status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to load building.</Alert>
      )}
      {!isNew && doc?.addressComponents?.isSlave && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={master && (
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`/dashboard/building-editor/${master.id}`)}
            >
              Open master
            </Button>
          )}
        >
          <strong>This is a slave record.</strong>
          {' Service qualification matches on the master record'}
          {master
            ? ` — “${master.address}” (status ${master.status || 'unknown'}). `
            : ` (id ${doc.addressComponents.masterBuildingId || 'unknown'}). `}
          Edits made here will not change what customers see; edit the master instead.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Address"
                  value={form.address}
                  onChange={setField('address')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Building Name"
                  value={form.buildingName}
                  onChange={setField('buildingName')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Unique Code"
                  value={form.uniqueCode}
                  onChange={setField('uniqueCode')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  select={statusOptions.length > 0}
                  size="small"
                  label="Status"
                  value={form.status}
                  onChange={setField('status')}
                >
                  {form.status === '' && (
                    <MenuItem value="" disabled><em>Select status…</em></MenuItem>
                  )}
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select={typeOptions.length > 0}
                  size="small"
                  label="Type"
                  value={form.legacyType}
                  onChange={setField('legacyType')}
                >
                  <MenuItem value=""><em>(none)</em></MenuItem>
                  {typeOptions.map((option) => (
                    <MenuItem key={option} value={option}>{option}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Building Type"
                  value={form.buildingType}
                  onChange={setField('buildingType')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Est. Service Delivery Time"
                  value={form.estimatedServiceDeliveryTime}
                  onChange={setField('estimatedServiceDeliveryTime')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Levels"
                  value={form.numberOfLevels}
                  onChange={setField('numberOfLevels')}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Dwellings"
                  value={form.numberOfDwellings}
                  onChange={setField('numberOfDwellings')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={<Checkbox checked={form.footprint} onChange={setField('footprint')} />}
                  label="In footprint"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.inductionRequired}
                      onChange={setField('inductionRequired')}
                    />
                  )}
                  label="Induction required"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.customerInstallApprovalRequired}
                      onChange={setField('customerInstallApprovalRequired')}
                    />
                  )}
                  label="Install approval required"
                />
              </Grid>
            </Grid>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={saveStatus === 'loading' || !form.address || !form.status}
                onClick={handleSave}
              >
                {isNew ? 'Create' : 'Save'}
              </Button>
              {!isNew && (
                <Button
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {!isNew && buildingId && (
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Master building</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Address alias: service qualification resolves to the
                master record. Any building can be picked as master.
              </Typography>
              <RelationList
                items={master ? [master] : []}
                emptyText="No master — this is a standalone (or master) record."
                unlinkTooltip="Clear master link"
                onOpen={(id) => navigate(`/dashboard/building-editor/${id}`)}
                onUnlink={() => runRelation(
                  clearMaster({ id: buildingId }),
                  'Master link cleared',
                )}
              />
              {!master && doc?.addressComponents?.masterBuildingId && (
                <Alert severity="error" sx={{ mb: 1 }}>
                  {`masterBuildingId points at ${doc.addressComponents.masterBuildingId}, `}
                  but that record could not be loaded — the link may be broken.
                </Alert>
              )}
              <BuildingSearchField
                label={master ? 'Re-point master…' : 'Set master…'}
                excludeIds={[buildingId, ...(master ? [master.id] : [])]}
                onPick={(b) => runRelation(
                  setMaster({ id: buildingId, masterId: b.id }),
                  'Master set',
                )}
              />
            </Paper>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Parents</Typography>
              <RelationList
                items={parents}
                emptyText="No parent buildings."
                unlinkTooltip="Detach parent"
                onOpen={(id) => navigate(`/dashboard/building-editor/${id}`)}
                onUnlink={(parentId) => runRelation(
                  detachParent({ id: buildingId, parentId }),
                  'Parent detached',
                )}
              />
              <BuildingSearchField
                label="Add parent…"
                excludeIds={relatedIds}
                onPick={(b) => runRelation(
                  attachParent({ id: buildingId, parentId: b.id }),
                  'Parent attached',
                )}
              />
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Children</Typography>
              <RelationList
                items={children}
                emptyText="No child buildings."
                unlinkTooltip="Detach child"
                onOpen={(id) => navigate(`/dashboard/building-editor/${id}`)}
                onUnlink={(childId) => runRelation(
                  detachChild({ id: buildingId, childId }),
                  'Child detached',
                )}
              />
              <BuildingSearchField
                label="Add child…"
                excludeIds={relatedIds}
                onPick={(b) => runRelation(
                  attachChild({ id: buildingId, childId: b.id }),
                  'Child attached',
                )}
              />
            </Paper>
            {slaves.length > 0 && (
              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Slave records</Typography>
                <Typography variant="body2" color="text.secondary">
                  Alternate address records pointing at this building as their
                  master. Service qualification serves this master record, not
                  the slaves.
                </Typography>
                <RelationList
                  items={slaves}
                  emptyText=""
                  onOpen={(id) => navigate(`/dashboard/building-editor/${id}`)}
                />
              </Paper>
            )}
          </Grid>
        )}
      </Grid>

      {!isNew && doc && (
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">
              Raw document (exactly what portal-backend serves for this building)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1,
                fontSize: '0.75rem',
                overflowX: 'auto',
                bgcolor: 'grey.100',
                borderRadius: 1,
              }}
            >
              {JSON.stringify(doc, null, 2)}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete building?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`This permanently removes “${form.address}” from the building database. `}
            Deletion is refused while other buildings still list it as a parent.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSnack(null)}>
          {snack}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuildingEditorDetail;
